import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  ExperimentOutlined,
  HeartOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsKlearlySuperAdmin } from "../access-control/accountSlice";
import GeneralTableHeader from "../common/general-table-header/GeneralTableHeader";
import StatusBanner from "./components/StatusBanner";
import HealthTimeline from "./components/HealthTimeline";
import IncidentCard from "./components/IncidentCard";
import IncidentHistoryTable from "./components/IncidentHistoryTable";
import SimulateEventModal from "./components/SimulateEventModal";
import { componentStateMeta } from "./paymentHealthDisplay";
import {
  fetchPaymentHealthStatusThunk,
  selectPaymentHealthError,
  selectPaymentHealthLastUpdatedKey,
  selectPaymentHealthLoading,
  selectPaymentHealthStatus,
} from "./paymentHealthSlice";

const POLL_INTERVAL_MS = 30_000;
const IS_PROD = process.env.REACT_APP_STAGE === "prod";

export default function PaymentHealth() {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectPaymentHealthStatus);
  const isLoading = useAppSelector(selectPaymentHealthLoading);
  const error = useAppSelector(selectPaymentHealthError);
  const lastUpdatedKey = useAppSelector(selectPaymentHealthLastUpdatedKey);
  const isSuperAdmin = useAppSelector(selectIsKlearlySuperAdmin);
  const [simulateOpen, setSimulateOpen] = useState(false);
  const canSimulate = isSuperAdmin && !IS_PROD;

  useEffect(() => {
    dispatch(fetchPaymentHealthStatusThunk());
    const id = setInterval(() => {
      dispatch(fetchPaymentHealthStatusThunk());
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [dispatch]);

  // Refetch immediately after a manual resolve / false-positive action.
  useEffect(() => {
    if (lastUpdatedKey) dispatch(fetchPaymentHealthStatusThunk());
  }, [dispatch, lastUpdatedKey]);

  const monitor = status?.monitor;

  return (
    <div>
      <GeneralTableHeader
        title="Status Page"
        description="Client-side payment error monitoring from Mixpanel. Auto-refreshes every 30s."
        avatarUrl={<HeartOutlined style={{ fontSize: "30px" }} />}
        actionButtons={[
          ...(canSimulate
            ? [
                <Button
                  key="simulate"
                  icon={<ExperimentOutlined />}
                  onClick={() => setSimulateOpen(true)}
                >
                  Simulate event
                </Button>,
              ]
            : []),
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            loading={isLoading}
            onClick={() => dispatch(fetchPaymentHealthStatusThunk())}
          >
            Refresh
          </Button>,
        ]}
      />

      {canSimulate && (
        <SimulateEventModal
          open={simulateOpen}
          onClose={() => setSimulateOpen(false)}
          onSubmitted={() => dispatch(fetchPaymentHealthStatusThunk())}
        />
      )}

      {error && !status && (
        <Alert
          type="error"
          showIcon
          message="Could not load payment health status"
          description={error}
          style={{ marginBottom: 24 }}
        />
      )}

      {!status && isLoading && (
        <div style={{ textAlign: "center", padding: 64 }}>
          <Spin size="large" />
        </div>
      )}

      {status && (
        <>
          <StatusBanner
            overallState={status.overallState}
            monitor={status.monitor}
            generatedAt={status.generatedAt}
          />

          <HealthTimeline />

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {status.components.map((c) => {
              const m = componentStateMeta(c.state);
              return (
                <Col xs={24} sm={12} md={8} key={c.component}>
                  <Card size="small">
                    <Space
                      style={{
                        width: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography.Text strong>{c.component}</Typography.Text>
                      <Badge color={m.color} text={m.label} />
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>

          {monitor && !monitor.healthy && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
              message="Monitor health degraded"
              description={
                <Space direction="vertical" size={2}>
                  <span>
                    {monitor.stale
                      ? "The alerter has not run recently."
                      : "Mixpanel event fetching is failing."}
                  </span>
                  <span>
                    Last run:{" "}
                    {monitor.lastRunAt
                      ? dayjs(monitor.lastRunAt).format("MMM D, HH:mm:ss")
                      : "never"}
                    {" | "}Mixpanel fetch:{" "}
                    {monitor.mixpanelFetchOk ? "ok" : "failing"}
                  </span>
                  {monitor.lastError && <span>Error: {monitor.lastError}</span>}
                </Space>
              }
            />
          )}

          <Typography.Title level={5}>
            Active incidents <Tag>{status.activeIncidents.length}</Tag>
          </Typography.Title>
          {status.activeIncidents.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No active incidents"
              style={{ marginBottom: 24 }}
            />
          ) : (
            status.activeIncidents.map((incident) => (
              <IncidentCard key={incident.id} incident={incident} />
            ))
          )}

          <Typography.Title level={5} style={{ marginTop: 24 }}>
            Recent incidents
          </Typography.Title>
          <IncidentHistoryTable incidents={status.recentIncidents} />
        </>
      )}
    </div>
  );
}
